import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, HostBinding } from '@angular/core';
import { trigger, state, style, animate, transition } from '@angular/animations';

export interface TabConfig {
  route: string;
  iconSvgPath: string;
  id: string;
}

@Component({
  selector: 'tab-bar',
  templateUrl: './tab-bar.component.html',
  styleUrls: ['./tab-bar.component.scss'],
  animations: [
    trigger('tabBarVisibility', [
      state('hidden', style({
        opacity: 0,
        transform: 'translateY(100vw)',
        height: '0px',
        padding: '0',
        pointerEvents: 'none'
      })),
      state('shown', style({
        opacity: 1,
        transform: 'translateY(0vw)',
        height: '25vw',
        padding: '0',
        pointerEvents: 'auto'
      })),
      transition('hidden <=> shown', [
        animate('1000ms ease-in-out')
      ])
    ]),
  ]
})
export class TabBarComponent implements OnInit, OnChanges {

  @Input() currentActiveRoute: string | null = null;
  @Output() tabClick = new EventEmitter<string>();

  tabs: TabConfig[] = [];

  @HostBinding('style.--x')
  bubbleXPosition: number = 0;

  @HostBinding('style.--num-tabs')
  numTabs: number = 0;

  private routeIndexMap: { [key: string]: number } = {};

  private allDefinedTabs: TabConfig[] = [
    { route: '/calendar', iconSvgPath: '/assets/svg/calendar.svg', id: 'nav-1' },
    { route: '/menu', iconSvgPath: '/assets/svg/menu-n.svg', id: 'nav-2' },
    { route: '/accounts', iconSvgPath: '/assets/svg/user.svg', id: 'nav-3' },
    { route: '/notifications', iconSvgPath: '/assets/svg/nbell.svg', id: 'nav-4' },
    { route: '/order', iconSvgPath: '/assets/svg/carts.svg', id: 'nav-5' },
    { route: '/onboarding', iconSvgPath: '/assets/svg/dasher.svg', id: 'nav-6' },
    { route: '/menu-list', iconSvgPath: '/assets/svg/menu-n.svg', id: 'nav-7' },
    { route: '/subscription-list', iconSvgPath: '/assets/svg/subscription.svg', id: 'nav-8' },
    { route: '/journey', iconSvgPath: '/assets/svg/apps.svg', id: 'nav-9' },
  ];

  private tabSets: { [currentRoute: string]: string[] } = {
    '/onboarding': ['/subscription-list', '/menu-list', '/onboarding', '/journey', '/order'],
    '/calendar': ['/calendar', '/menu', '/accounts', '/notifications', '/order'],
    '/menu': ['/calendar', '/menu', '/accounts', '/notifications', '/order'],
    '/accounts': ['/calendar', '/menu', '/accounts', '/notifications', '/order'],
    '/notifications': ['/calendar', '/menu', '/accounts', '/notifications', '/order'],
    '/order': ['/calendar', '/menu', '/accounts', '/notifications', '/order'],
    '/journey': ['/calendar', '/menu', '/accounts', '/notifications', '/order'],
    '/menu-list': ['/menu', '/subscription-list', '/accounts', '/notifications', '/order'],
    '/subscription-list': ['/subscription-list', '/menu-list', '/accounts', '/notifications', '/journey'],
  };

  constructor() { }

  ngOnInit() {
    this.allDefinedTabs.forEach((tab, index) => {
      tab.id = `nav-${index + 1}`;
      this.routeIndexMap[tab.route] = index;
    });

    if (this.currentActiveRoute) {
      this.updateActiveTabs(this.currentActiveRoute);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentActiveRoute']) {
      const newRoute = changes['currentActiveRoute'].currentValue;
      const oldRoute = changes['currentActiveRoute'].previousValue;

      // Always call updateActiveTabs and updateBubblePosition to handle visibility changes
      // and ensure the correct tabs/bubble are shown/hidden.
      this.updateActiveTabs(newRoute);
      this.updateBubblePosition(newRoute);

      if (newRoute === null) {
            this.tabs = [];
      this.numTabs = 0;
        console.warn('TabBarComponent: currentActiveRoute received null value.');
      }
    }
  }

  @HostBinding('@tabBarVisibility')
  get tabBarAnimationState(): string {
    const shouldShow = this.getShouldShowTabBar();
    return shouldShow ? 'shown' : 'hidden';
  }

  private getShouldShowTabBar(): boolean {
    if (this.currentActiveRoute === null) {
      return false;
    }
    const cleanedRoute = this.currentActiveRoute.split('?')[0].split('#')[0];

    return [
      '/onboarding',
      '/calendar',
      '/menu',
      '/accounts',
      '/order',
      '/notifications',
    ].includes(cleanedRoute);
  }

  private updateActiveTabs(activeRoute: string | null): void {
    const shouldShow = this.getShouldShowTabBar();

    if (!shouldShow || activeRoute === null) {
      this.tabs = [];
      this.numTabs = 0;
      console.log('TabBarComponent: Tab bar is hidden, clearing tabs.');
      return;
    }

    const cleanedRoute = activeRoute.split('?')[0].split('#')[0];
    const desiredTabRoutes = this.tabSets[cleanedRoute];

    if (desiredTabRoutes && desiredTabRoutes.length > 0) {
      this.tabs = desiredTabRoutes
        .map(route => {
          const index = this.routeIndexMap[route];
          return this.allDefinedTabs[index];
        })
        .filter(tab => tab !== undefined);
    } else {
      console.warn(`TabBarComponent: No specific tab set defined for route: ${cleanedRoute}. Falling back to default tabs.`);
      // If no specific set is defined but the bar is still supposed to be shown,
      // you might want a "catch-all" default or simply clear them.
      // For this scenario, if the bar is shown but no specific set, we'll default to the first 5.
      this.tabs = this.allDefinedTabs.slice(0, 5);
    }

    this.numTabs = this.tabs.length;
    console.log('TabBarComponent: Active Tabs for route', activeRoute, ':', this.tabs);
  }

  private updateBubblePosition(activeRoute: string | null): void {
    // If the tab bar is hidden, or no route is active, reset bubble position.
    if (!this.getShouldShowTabBar() || activeRoute === null) {
      this.bubbleXPosition = 0; // Or any default position
      return;
    }

    const cleanedRoute = activeRoute.split('?')[0].split('#')[0];
    const activeTabIndex = this.tabs.findIndex(tab => tab.route === cleanedRoute);
    this.bubbleXPosition = (activeTabIndex !== -1) ? activeTabIndex : 0;
  }

  isTabChecked(route: string): boolean {
    if (this.currentActiveRoute === null) {
      return false;
    }
    return this.currentActiveRoute.split('?')[0].split('#')[0] === route;
  }

  onTabClick(route: string): void {
    this.tabClick.emit(route);
  }
}